"use server";

import { cookies } from "next/headers";
import { createAdminClient } from "@/lib/supabase/admin";
import { requireActualStaff } from "@/lib/auth/session";
import {
  clearClientModeSelection,
  writeClientModeSelection,
} from "@/lib/auth/client-mode-cookie";
import type {
  ClientModeAccountOption,
  ClientModePreset,
} from "@/lib/auth/client-mode-shared";
import { CLIENT_PERMISSION_PRESET_KEYS } from "@/lib/auth/client-permissions";

const PRESETS = new Set<string>(
  CLIENT_PERMISSION_PRESET_KEYS.filter((preset) => preset !== "custom"),
);

export async function listClientModeAccountsAction(): Promise<ClientModeAccountOption[]> {
  await requireActualStaff();
  const admin = createAdminClient();
  const [{ data: profiles }, { data: assignments }] = await Promise.all([
    admin.from("profiles").select("id, name, email").eq("role", "client").order("name").order("email"),
    admin.from("client_users").select("user_id"),
  ]);
  const counts = new Map<string, number>();
  for (const row of assignments ?? []) {
    counts.set(row.user_id, (counts.get(row.user_id) ?? 0) + 1);
  }
  return (profiles ?? []).map((profile) => ({
    id: profile.id,
    name: profile.name,
    email: profile.email,
    companyCount: counts.get(profile.id) ?? 0,
  }));
}

export async function enablePresetClientModeAction(
  preset: ClientModePreset,
): Promise<{ destination: string }> {
  const session = await requireActualStaff();
  if (!PRESETS.has(preset)) throw new Error("Invalid client permission preset.");

  await writeClientModeSelection({
    version: 1,
    actorUserId: session.user.id,
    kind: "preset",
    preset,
  });
  (await cookies()).delete("spark-demo");
  console.info("[client-mode] enabled preset", { actorUserId: session.user.id, preset });

  return { destination: "/" };
}

export async function enableAccountClientModeAction(
  subjectUserId: string,
): Promise<{ destination: string }> {
  const session = await requireActualStaff();
  const admin = createAdminClient();
  const { data: subject } = await admin
    .from("profiles")
    .select("id")
    .eq("id", subjectUserId)
    .eq("role", "client")
    .maybeSingle();
  if (!subject) throw new Error("Client account not found.");

  await writeClientModeSelection({
    version: 1,
    actorUserId: session.user.id,
    kind: "account",
    subjectUserId,
  });
  (await cookies()).delete("spark-demo");
  console.info("[client-mode] enabled account", { actorUserId: session.user.id, subjectUserId });

  return { destination: "/" };
}

export async function disableClientModeAction(): Promise<{ destination: string }> {
  const session = await requireActualStaff();
  await clearClientModeSelection();
  console.info("[client-mode] disabled", { actorUserId: session.user.id });
  return { destination: "/" };
}
